import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

// Ohne Boundary reißt ein einzelner Renderfehler den kompletten React-Baum ab: die Seite bleibt
// weiß, der Nutzer bekommt keine Meldung, nur die Browser-Konsole hat einen Eintrag. Reproduziert
// am 21.07.2026 — der api-Container lief noch mit altem Code und lieferte `/plans/:id/progress`
// ohne `open_defects`, `defects.length` warf in <DefectChips>, und damit verschwand die gesamte
// Testplan-Detailseite inklusive Kopfkarten und Scope-Tabelle. Jede Versions-Schieflage zwischen
// API und Frontend (Deploy-Reihenfolge, Browser-Cache, Rollback) kann denselben Effekt haben.
//
// Zwei Ebenen:
// - `variant="page"` um die <Routes> herum → die Navigation bleibt bedienbar.
// - `variant="section"` je größerem Seitenabschnitt → ein kaputter Abschnitt nimmt seine
//   Geschwister nicht mit.

type ErrorBoundaryVariant = 'page' | 'section';

// Eigene Funktionskomponente, weil Klassen keine Hooks (und damit kein useTranslation) können.
function ErrorFallback({ variant }: { variant: ErrorBoundaryVariant }) {
  const { t } = useTranslation();
  const isPage = variant === 'page';
  return (
    <div
      role="alert"
      className={`rounded-card border border-[var(--border)] bg-bg-surface ${
        isPage ? 'm-6 p-6' : 'p-4'
      }`}
    >
      <p className="text-sm font-semibold text-text-primary">
        {t(isPage ? 'errorBoundary.title' : 'errorBoundary.sectionTitle')}
      </p>
      <p className="mt-1 text-xs text-text-muted">{t('errorBoundary.body')}</p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="mt-3 rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white"
      >
        {t('errorBoundary.reload')}
      </button>
    </div>
  );
}

type ErrorBoundaryProps = { children: ReactNode; variant?: ErrorBoundaryVariant };
type ErrorBoundaryState = { hasError: boolean };

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  // Fehler und Component-Stack gehören in die Konsole, nicht in die Fallback-UI: Endnutzer
  // können mit einem Stacktrace nichts anfangen, für den Support ist er im Browser abrufbar.
  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  override render() {
    if (this.state.hasError) {
      return <ErrorFallback variant={this.props.variant ?? 'section'} />;
    }
    return this.props.children;
  }
}
